import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Tent, Map, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { READY_GUIDES } from "@/lib/guides";

function RailBlock({ icon: Icon, title, to, testid, children }) {
  return (
    <section className="py-6 border-b border-[var(--hh-border)] last:border-b-0" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="label-eyebrow flex items-center gap-2 text-[var(--hh-accent)]"><Icon size={12}/> {title}</p>
        <Link to={to} className="link-hh text-xs label-eyebrow flex items-center gap-1" data-testid={`${testid}-all`}>All <ArrowRight size={11}/></Link>
      </div>
      <div className="mt-4 space-y-3">{children}</div>
    </section>
  );
}

function RailItem({ to, title, meta, testid }) {
  return (
    <Link to={to} className="block group" data-testid={testid}>
      <p className="text-sm text-[var(--hh-cream)] group-hover:text-[var(--hh-accent)] transition-colors line-clamp-2">{title}</p>
      {meta && <p className="text-xs text-[var(--hh-sage)] mt-0.5">{meta}</p>}
    </Link>
  );
}

const nextStop = (trip, today) =>
  (trip.stops || []).filter((s) => s.start_date && s.start_date >= today).sort((a, b) => a.start_date.localeCompare(b.start_date))[0];

export default function RightRail() {
  const [ads, setAds] = useState([]);
  const [trips, setTrips] = useState([]);

  useEffect(() => {
    api.get("/ads?limit=4").then((r) => setAds(r.data || [])).catch(() => setAds([]));
    const today = new Date().toISOString().slice(0, 10);
    api.get("/travel/me").then((r) => {
      const upcoming = (r.data || [])
        .map((t) => ({ t, s: nextStop(t, today) }))
        .filter((x) => x.s)
        .sort((a, b) => a.s.start_date.localeCompare(b.s.start_date))
        .slice(0, 3);
      setTrips(upcoming);
    }).catch(() => setTrips([]));
  }, []);

  return (
    <aside className="px-6 xl:px-5 border-t xl:border-t-0 xl:border-l border-[var(--hh-border)] xl:sticky xl:top-14 xl:max-h-[calc(100vh-3.5rem)] xl:overflow-y-auto scroll-area" data-testid="right-rail">
      <RailBlock icon={BookOpen} title="Latest guides" to="/guides" testid="rail-guides">
        {READY_GUIDES.map((g) => (
          <RailItem key={g.slug} to={`/guides/${g.slug}`} title={g.title} meta={`${g.category} · ${g.readMinutes} min read`} testid={`rail-guide-${g.slug}`} />
        ))}
      </RailBlock>
      <RailBlock icon={Tent} title="Newest ads" to="/community-ads" testid="rail-ads">
        {ads.length === 0 && <p className="text-xs text-[var(--hh-sage)]" data-testid="rail-ads-empty">No ads yet.</p>}
        {ads.map((a) => (
          <RailItem key={a.id} to={`/community-ads/${a.id}`} title={a.title} meta={a.location} testid={`rail-ad-${a.id}`} />
        ))}
      </RailBlock>
      <RailBlock icon={Map} title="Your upcoming trips" to="/travel" testid="rail-trips">
        {trips.length === 0 && (
          <Link to="/travel" className="text-xs link-hh" data-testid="rail-trips-empty">No upcoming trips — plan one</Link>
        )}
        {trips.map(({ t, s }) => (
          <RailItem key={t.id} to={`/travel?trip=${t.id}`} title={t.title} meta={`${s.location} · ${s.start_date}`} testid={`rail-trip-${t.id}`} />
        ))}
      </RailBlock>
    </aside>
  );
}
